import { FC, MouseEvent, useId } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { EntityTeamItem, entityTeamLegalRole } from '@/api/entity/types';
import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import DeleteIcon from '@/assets/svg/ui-elements/delete-outline.svg';
import PencilIcon from '@/assets/svg/ui-elements/pencil.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import { IconButton, Tooltip, VisuallyHidden } from '@/shared/design-system';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';

import { MemberActionsRow } from './user-details.styled';

export const LAST_ADMIN_TOOLTIP =
  'The last Admin of the entity cannot be removed. Assign another Admin first.';
export const EDIT_BLOCKED_TOOLTIP = 'You do not have permission to edit a team member.';
export const REMOVE_BLOCKED_TOOLTIP = 'You do not have permission to remove a team member.';

type MemberActionsProps = {
  data: EntityTeamItem;
  userId: string;
  entityId?: string | null;
  isLastAdmin?: boolean;
};

export const MemberActions: FC<MemberActionsProps> = ({
  data,
  userId,
  entityId,
  isLastAdmin = false,
}) => {
  const navigate = useNavigate();
  const lastAdminHintId = `member-remove-hint-${useId()}`;

  const canManageTeam = useCan(Permission.EntityManageTeam);
  const canViewRoles = useCan(Permission.EntityViewRoles);
  const canAssignRoles = useCan(Permission.EntityAssignRoles);
  const canAssignMemberRole =
    !!data.entityMemberId && !!data.userId && canViewRoles && canAssignRoles;
  const canEditMember = canManageTeam || canAssignMemberRole;

  const isRejectedUser = data.status === 'Rejected';
  const editTooltip = canEditMember ? 'Edit' : EDIT_BLOCKED_TOOLTIP;
  const removeTooltip = !canManageTeam
    ? REMOVE_BLOCKED_TOOLTIP
    : isLastAdmin
      ? LAST_ADMIN_TOOLTIP
      : 'Remove';

  const { handleOpen: openEditModal } = useGlobalModal('TEAM_MEMBER_EDIT_MODAL', {
    email: data.email,
    legalRole: data.isDirector ? entityTeamLegalRole.Director : entityTeamLegalRole.TeamMember,
    role: data.role,
    memberId: data?.entityMemberId,
    userId: data.userId,
    invitationId: data?.entityInvitationId,
    entityId,
  });

  const { handleOpen: openDeleteModal } = useGlobalModal('TEAM_MEMBER_DELETE_MODAL', {
    memberId: data?.entityMemberId,
    invitationId: data?.entityInvitationId,
    dataUserId: data.userId,
    isDirector: data?.isDirector,
    userId,
    entityId,
  });

  const runAction = (event: MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
  };

  return (
    <MemberActionsRow onClick={(event) => event.stopPropagation()}>
      <Tooltip title={editTooltip} side="top">
        <DeniedReasonTrigger denied={isRejectedUser || !canEditMember}>
          <IconButton
            size="sm"
            color="tertiary"
            variant="outline"
            aria-label="Edit team member"
            disabled={isRejectedUser || !canEditMember}
            onClick={(event) => runAction(event, openEditModal)}
          >
            <PencilIcon />
          </IconButton>
        </DeniedReasonTrigger>
      </Tooltip>

      <Tooltip title={removeTooltip} side="top">
        <DeniedReasonTrigger denied={isRejectedUser || !canManageTeam || isLastAdmin}>
          <IconButton
            size="sm"
            color="tertiary"
            variant="outline"
            aria-label="Remove team member"
            aria-describedby={isLastAdmin ? lastAdminHintId : undefined}
            disabled={isRejectedUser || !canManageTeam || isLastAdmin}
            onClick={(event) => runAction(event, openDeleteModal)}
          >
            <DeleteIcon />
          </IconButton>
        </DeniedReasonTrigger>
      </Tooltip>
      {isLastAdmin && <VisuallyHidden id={lastAdminHintId}>{LAST_ADMIN_TOOLTIP}</VisuallyHidden>}

      <Tooltip title="Open profile" side="top">
        <IconButton
          size="sm"
          color="tertiary"
          variant="outline"
          aria-label="Open team member profile"
          disabled={!data?.userId || isRejectedUser}
          onClick={(event) =>
            runAction(event, () =>
              navigate(generatePath(PagePaths.userProfile, { id: data.userId ?? '' })),
            )
          }
        >
          <ArrowRightIcon width={16} height={16} />
        </IconButton>
      </Tooltip>
    </MemberActionsRow>
  );
};
